<template>
  <div class="cc-my-account-container">
    <div v-if="renewalReminderNotification && renewalReminderNotification.daysLeft >= -1" class="renewal-reminder-toast" role="status" aria-live="polite">
      <div class="renewal-reminder-message">
        {{ t(renewalReminderMessageKey, { days: renewalReminderNotification.daysLeft }) }}
      </div>
      <div class="renewal-reminder-actions">
        <button class="renewal-reminder-action-btn" @click="handleGoToWebshopSettings">
          {{ t('consultantCorner.myAccountRenewalReminder.action') }}
        </button>
        <button class="renewal-reminder-dismiss-btn" @click="dismissRenewalReminder">
          {{ t('consultantCorner.myAccountRenewalReminder.dismiss') }}
        </button>
      </div>
    </div>

    <div class="cc-header">
      <h2>{{ t('consultantCorner.MyAccount') }}</h2>
    </div>

    <!-- Mobile Tab Dropdown (shown only on mobile) -->
    <div class="mobile-tab-dropdown">
      <label for="mobile-tab-select">{{ t('consultantCorner.SelectSection') }}</label>
      <select
        id="mobile-tab-select"
        v-model="selectedTab"
        @change="onMobileTabChange($event)"
        class="form-control"
      >
        <option
          v-for="(tab, index) in tabs"
          :key="index"
          :value="index"
        >
          {{ tab.title }}
        </option>
      </select>
    </div>

    <!-- Desktop Tab Strip (hidden on mobile) -->
    <kTabStrip @select="onTabSelect" :selected="selectedTab" tab-position="left" class="cc-my-account-tabs desktop-tabs">
      <TabStripTab :title="t('consultantCorner.ContactDetails')">
        <CCMAContactDetails
          v-if="selectedTab === tabIndex('contactDetails')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.companyName.CompanyName')">
        <CCMACompanyName
          v-if="selectedTab === tabIndex('companyName')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.Address')">
        <CCMAAddress
          v-if="selectedTab === tabIndex('address')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.VATLiability')">
        <CCMAVATLiability
          v-if="selectedTab === tabIndex('vatLiability')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.UploadPhoto')">
        <CCMAUploadPhoto
          v-if="selectedTab === tabIndex('uploadPhoto')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.MyStory')">
        <!-- Show loading overlay for MyStory tab -->
        <div v-if="selectedTab === tabIndex('myStory') && tabLoading" class="tab-loading-overlay">
          <div class="tab-loading-container">
            <Loader type="infinite-spinner" :size="'large'" />
            <p class="tab-loading-text">{{ t('consultantCorner.myStory.LoadingStory') }}</p>
          </div>
        </div>
        <CCMAMyStory
          v-if="selectedTab === tabIndex('myStory')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.SocialMedia')">
        <CCMASocialMedia
          v-if="selectedTab === tabIndex('socialMedia')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <!-- WebshopSettings tab - PWS/Trial subscription does not apply to French consultants -->
      <TabStripTab v-if="countryCode !== 'FR'" :title="t('consultantCorner.WebshopSettings')">
        <CCMAWebshopSettings
          v-if="selectedTab === tabIndex('webshopSettings')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.security.passwordHeader')">
        <CCMAPasswordChange
          v-if="selectedTab === tabIndex('passwordChange')"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.securityQuestions.header')">
        <CCMASecurityQuestions
          v-if="selectedTab === tabIndex('securityQuestions')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>
      <!-- Temporarily hide incomplete tabs -->
       <TabStripTab :title="t('consultantCorner.RecruitmentSearch')">
        <CCMARecruitmentSearch
          v-if="selectedTab === tabIndex('recruitmentSearch')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.Contracts')">
        <CCMAContracts
          v-if="selectedTab === tabIndex('contracts')"
          :business-details="businessDetails"
        />
      </TabStripTab>

      <!-- Temporarily hide incomplete tabs -->
       <TabStripTab :title="t('consultantCorner.WalletInformation')">
        <CCMAWalletInformation v-if="selectedTab === tabIndex('walletInformation')" />
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.Banking')">
        <CCMABanking v-if="selectedTab === tabIndex('banking')" />
      </TabStripTab>

      <!-- French Contribution tab - only show for French consultants -->
      <TabStripTab v-if="countryCode === 'FR'" :title="t('consultantCorner.FrenchContribution')">
        <CCMAFrenchContribution
          v-if="selectedTab === tabIndex('frenchContribution')"
          :business-details="businessDetails"
          @update-details="updateBusinessDetails"
        />
      </TabStripTab>
    </kTabStrip>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '../../logger.ts'
import { TabStrip as kTabStrip, TabStripTab } from '@progress/kendo-vue-layout'
import { Loader } from '@progress/kendo-vue-indicators'
import { GetBusinessDetails, TpcRenewalStatusGet } from '../../api/ConsultantCorner'
import { GetBeeNumberAuthenicatedUser, GetCountryCode } from '../../stores/SettingsStore'
import { handleError } from '../../services/ErrorService'
import type { IBusinessDetails } from '../../types/ConsultantCornerTypes'

import CCMAContactDetails from './CCMAContactDetails.vue'
import CCMACompanyName from './CCMACompanyName.vue'
import CCMAAddress from './CCMAAddress.vue'
import CCMAVATLiability from './CCMAVATLiability.vue'
import CCMAUploadPhoto from './CCMAUploadPhoto.vue'
import CCMAMyStory from './CCMAMyStory.vue'
import CCMARecruitmentSearch from './CCMARecruitmentSearch.vue'
import CCMAWebshopSettings from './CCMAWebshopSettings.vue'
import CCMAPasswordChange from './CCMAPasswordChange.vue'
import CCMASecurityQuestions from './CCMASecurityQuestions.vue'
import CCMASocialMedia from './CCMASocialMedia.vue'
import CCMAContracts from './CCMAContracts.vue'
import CCMAWalletInformation from './CCMAWalletInformation.vue'
import CCMABanking from './CCMABanking.vue'
import CCMAFrenchContribution from './CCMAFrenchContribution.vue'

const { t } = useI18n()
const logger = useLogger()
setStoreLogger(logger)
const selectedTab = ref(0)
const businessDetails = ref<IBusinessDetails | null>(null)
const loading = ref(false)
const tabLoading = ref(false)
const countryCode = ref('')
const renewalReminderNotification = ref<{ daysLeft: number; programType: 'trial' | 'premium' } | null>(null)

const renewalReminderMessageKey = computed(() => {
  if (!renewalReminderNotification.value) return ''
  const isTrial = renewalReminderNotification.value.programType === 'trial'
  const { daysLeft } = renewalReminderNotification.value
  if (daysLeft === -1) return isTrial ? 'consultantCorner.myAccountRenewalReminder.trialMessageExpired' : 'consultantCorner.myAccountRenewalReminder.messageExpired'
  if (daysLeft === 0) return isTrial ? 'consultantCorner.myAccountRenewalReminder.trialMessageToday' : 'consultantCorner.myAccountRenewalReminder.messageToday'
  return isTrial ? 'consultantCorner.myAccountRenewalReminder.trialMessage' : 'consultantCorner.myAccountRenewalReminder.message'
})

// Ordered tab keys as rendered in the desktop TabStrip. WebshopSettings (PWS/Trial
// subscription) does not apply to French consultants, so it's excluded for them;
// FrenchContribution is only relevant to French consultants.
const TAB_KEYS = computed(() => {
  const keys = ['contactDetails', 'companyName', 'address', 'vatLiability', 'uploadPhoto', 'myStory', 'socialMedia']
  
  if (countryCode.value !== 'FR') {
    keys.push('webshopSettings')
  }
  keys.push('passwordChange', 'securityQuestions', 'recruitmentSearch', 'contracts', 'walletInformation', 'banking')
  if (countryCode.value === 'FR') {
    keys.push('frenchContribution')
  }
  return keys
})

const tabIndex = (key: string) => TAB_KEYS.value.indexOf(key)

const TAB_TITLES: Record<string, string> = {
  contactDetails: 'consultantCorner.ContactDetails',
  companyName: 'consultantCorner.companyName.CompanyName',
  address: 'consultantCorner.Address',
  vatLiability: 'consultantCorner.VATLiability',
  uploadPhoto: 'consultantCorner.UploadPhoto',
  myStory: 'consultantCorner.MyStory',
  socialMedia: 'consultantCorner.SocialMedia',
  webshopSettings: 'consultantCorner.WebshopSettings',
  passwordChange: 'consultantCorner.security.passwordHeader',
  securityQuestions: 'consultantCorner.securityQuestions.header',
  recruitmentSearch: 'consultantCorner.RecruitmentSearch',
  contracts: 'consultantCorner.Contracts',
  walletInformation: 'consultantCorner.WalletInformation',
  banking: 'consultantCorner.Banking',
  frenchContribution: 'consultantCorner.FrenchContribution',
}

// Tabs array for mobile dropdown
const tabs = ref<{ title: string }[]>([])

// Initialize tabs after i18n is ready; must mirror the desktop TabStrip's rendered order/filters
const initTabs = () => {
  tabs.value = TAB_KEYS.value.map((key) => ({ title: t(TAB_TITLES[key]) }))
}

// Handle mobile dropdown selection
const onMobileTabChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const index = parseInt(target.value)
  selectedTab.value = index

  // Show loading for tabs that need to load data
  const tabsWithLoading = [5] // CCMAMyStory tab index
  if (tabsWithLoading.includes(index)) {
    tabLoading.value = true
    setTimeout(() => {
      tabLoading.value = false
    }, 2000)
  }
}

const onTabSelect = (...args: any[]) => {
  const event = args[0]
  const newTabIndex = event.selected

  // Show loading for tabs that need to load data
  const tabsWithLoading = [5] // CCMAMyStory tab index
  if (tabsWithLoading.includes(newTabIndex)) {
    tabLoading.value = true
    // Hide loading after component has had time to mount and load
    setTimeout(() => {
      tabLoading.value = false
    }, 2000)
  }

  selectedTab.value = newTabIndex
}

const updateBusinessDetails = (updatedDetails: Partial<IBusinessDetails>) => {
  if (businessDetails.value) {
    businessDetails.value = { ...businessDetails.value, ...updatedDetails }
  }
}

const calculateDaysUntilRenewal = (renewalDate: Date | string): number => {
  const renewal = new Date(renewalDate)
  const today = new Date()
  const renewalStartOfDay = new Date(renewal.getFullYear(), renewal.getMonth(), renewal.getDate())
  const todayStartOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const millisecondsPerDay = 1000 * 60 * 60 * 24

  return Math.ceil((renewalStartOfDay.getTime() - todayStartOfDay.getTime()) / millisecondsPerDay)
}

const dismissRenewalReminder = () => {
  renewalReminderNotification.value = null
}

const handleGoToWebshopSettings = () => {
  selectedTab.value = tabIndex('webshopSettings')
  dismissRenewalReminder()
}

const checkRenewalReminderNotification = async () => {
  // PWS/Trial renewal does not apply to French consultants
  if (countryCode.value === 'FR') return

  try {
    const response = await TpcRenewalStatusGet()
    const renewalDate = response.item?.renewalDate
    const programId = response.item?.programId?.toLowerCase()

    if (!renewalDate || (programId !== 'trial' && programId !== 'premium')) return

    const daysLeft = calculateDaysUntilRenewal(renewalDate)

    if (daysLeft >= -1 && daysLeft <= 7) {
      renewalReminderNotification.value = { daysLeft, programType: programId }
    } else {
      renewalReminderNotification.value = null
    }
  } catch (error) {
    logger.warn('Unable to evaluate renewal reminder notification:', error)
  }
}

onMounted(async () => {
  try {
    loading.value = true

    // Load country code to determine if French Contribution tab should be shown
    try {
      countryCode.value = await GetCountryCode()
      console.info('Country code loaded:', countryCode.value)
    } catch (error) {
      console.error('Error loading country code:', error)
      countryCode.value = 'US' // Default fallback
    }

    // Initialize tabs after country code is loaded
    initTabs()

    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (beeNumber) {
      const response = await GetBusinessDetails(beeNumber, t)
      if (response) {
        businessDetails.value = response.businessDetails
      }
    }

    await checkRenewalReminderNotification()
  } catch (error) {
    console.error('Error loading business details:', error)
    await handleError(error, t)
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" scoped>
.cc-my-account-container {
  width: 100%;
  max-width: 100%;
}

.renewal-reminder-toast {
  position: fixed;
  top: $spacing-4;
  right: $spacing-4;
  z-index: $z-notification;
  max-width: 360px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-left: 4px solid var(--color-teal);
  border-radius: $radius-md;
  padding: $spacing-4;
  box-shadow: 0 4px 12px rgba(black, 0.12);
}

.renewal-reminder-message {
  @include typography-small;
  color: var(--color-text);
  margin-bottom: $spacing-3;
}

.renewal-reminder-actions {
  display: flex;
  gap: $spacing-2;
}

.renewal-reminder-action-btn,
.renewal-reminder-dismiss-btn {
  @include typography-small;
  border: 1px solid var(--color-border);
  border-radius: $button-radius;
  padding: $button-padding-sm-y $button-padding-sm-x;
  cursor: pointer;
}

.renewal-reminder-action-btn {
  background: var(--color-teal);
  color: var(--color-background);
  border-color: var(--color-teal);
}

.renewal-reminder-dismiss-btn {
  background: var(--color-background-soft);
  color: var(--color-text);
}

.cc-header {
  background: linear-gradient(135deg, var(--vt-c-white-soft) 0%, #e9ecef 100%);
  color: #495057;
  padding: $spacing-5 $spacing-8;
  border-radius: $radius-lg;
  margin-bottom: 32px;
  box-shadow: 0 2px 12px rgba(black, 0.1);
  border: 1px solid #dee2e6;
  position: relative;
  overflow: hidden;
}

.cc-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.cc-header h2 {
  @include typography-h3;
  @include font-weight-semibold;
  margin: 0;
  text-align: center;
  letter-spacing: -0.025em;
  color: #2c3e50;
}

.cc-my-account-tabs {
  min-height: 600px;
  width: 100%;
}

.cc-my-account-tabs :deep(.k-animation-container) {
  width: 100% !important;
  max-width: none !important;
}

.cc-my-account-tabs :deep(.k-animation-container-relative) {
  width: 100% !important;
  max-width: none !important;
}

.cc-my-account-tabs :deep(.k-animation-container.k-animation-container-relative.k-fade-appear) {
  display: none !important;
}

/* Mobile Tab Dropdown Styles */
.mobile-tab-dropdown {
  display: none;
  margin-bottom: 1.5rem;
  padding: 0 1rem;
}

.mobile-tab-dropdown label {
  @include typography-body;
  @include font-weight-semibold;
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
}

.mobile-tab-dropdown select {
  @include typography-body;
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  border-radius: $radius-md;
  background-color: var(--vt-c-white);
  color: #333;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
}

.mobile-tab-dropdown select:focus {
  outline: none;

  box-shadow: 0 0 0 3px rgba(#667eea, 0.15);
}

@media (max-width: 767px) {
  /* Show mobile dropdown, hide desktop tab strip items */
  .mobile-tab-dropdown {
    display: block;
  }

  .desktop-tabs :deep(.k-tabstrip-items) {
    display: none !important;
  }

  .cc-my-account-tabs {
    min-height: 400px;
  }
}

/* Tab loading overlay styles */
.tab-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(white, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $z-notification;
  border-radius: $radius-md;
}

.tab-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.tab-loading-text {
  @include typography-body;
  @include font-weight-medium;
  margin: 0;
  color: #666;
}

@media (min-width: 768px) {
  .cc-my-account-tabs .k-tabstrip-items {
    min-width: 200px;
    max-width: 250px;
  }
}
</style>
